import React, { Suspense, useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { Button, Column, Layout, Tabs, Stack } from "@/components/ui";
import { useModalContext } from "@/contexts/modal-context";
import LinkFooter from "@/features/ui/components/link-footer";
import {
  MyGroupsPanel,
  NewGroupPanel,
  SuggestedGroupsPanel,
} from "@/features/ui/util/async-components";
import { useSelectGroupsTab } from "@/hooks";
import { GroupsTabItems } from "@/hooks/useSelectGroupsTab";

const messages = defineMessages({
  title: { id: "groups.title", defaultMessage: "Groups" },
  explore: { id: "groups.tab-bar.explore", defaultMessage: "Explore" },
  myGroups: { id: "groups.tab-bar.my-groups", defaultMessage: "My Groups" },
  createGroup: { id: "groups.create-group", defaultMessage: "Create Group" },
});

interface GroupsPageProps {
  children: React.ReactNode;
}

/** Page to display groups. */
const GroupsPage: React.FC<GroupsPageProps> = ({ children }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { openModal } = useModalContext();
  const { selectedTab, setSelectedTab } = useSelectGroupsTab();

  const createGroup = () => {
    openModal("CREATE_GROUP");
  };

  const tabItems: React.ComponentProps<typeof Tabs>["items"] = useMemo(
    () => [
      {
        label: intl.formatMessage(messages.explore),
        action: () => {
          navigate("/groups/discover");
          setSelectedTab(GroupsTabItems.FIND_GROUPS);
        },
        name: GroupsTabItems.FIND_GROUPS,
      },
      {
        label: intl.formatMessage(messages.myGroups),
        action: () => {
          navigate("/groups");
          setSelectedTab(GroupsTabItems.MY_GROUPS);
        },
        name: GroupsTabItems.MY_GROUPS,
      },
    ],
    [],
  );

  return (
    <>
      <Layout.Main>
        <Column
          label={intl.formatMessage(messages.title)}
          withBackButton={false}
          content="list"
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={createGroup}
              className="hidden lg:flex xl:hidden"
            >
              {intl.formatMessage(messages.createGroup)}
            </Button>
          }
        >
          <Stack space={4}>
            <Tabs items={tabItems} activeItem={selectedTab} />

            {children}
          </Stack>
        </Column>
      </Layout.Main>

      <Layout.Aside>
        <Suspense fallback={null}>
          <NewGroupPanel />
        </Suspense>

        <Suspense fallback={null}>
          <SuggestedGroupsPanel />
        </Suspense>

        <Suspense fallback={null}>
          <MyGroupsPanel />
        </Suspense>

        <LinkFooter />
      </Layout.Aside>
    </>
  );
};

export default GroupsPage;
